import { useQueries } from "@tanstack/react-query";
import { useWatch } from "react-hook-form";

import { deserializeJsonApi } from "src/api/jsonApiHelpers";
import { CreateCaseFormValues } from "src/cases/createCase/createCaseSteps";
import { casesKeys } from "src/cases/queries/cases";
import { CasesDeserialized } from "src/cases/queries/casesTypes";
import { useCasesClient } from "src/cases/queries/client";
import {
  CasePropertiesValue,
  CasesResponse,
  GetCasesV2ApiV2CasesGetFilterEnvEnum,
} from "src/clients/case-management";
import { useEntityDisplayValues } from "src/entities/useEntityDisplayValues";
import { useEnvironment } from "src/hooks/useEnvironment";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export type DeserializedCase = CasesDeserialized["data"][number];

const STALE_TIME = 1000 * 60 * 5;

type MappedEntity = {
  entityType: string;
  entityId: string;
};

type QuerySpec = {
  entityType: string;
  entityId: string;
  propertyKey: string;
};

export type PropertyCaseGroup = {
  propertyKey: string;
  propertyDisplayName: string;
  cases: DeserializedCase[];
};

export type EntityWithOpenCases = {
  entityId: string;
  entityType: string;
  displayName: string;
  groups: PropertyCaseGroup[];
};

const CLOSED_STATUS = "closed";

const deserializeOpenCases = (
  data: Pick<CasesResponse, "data" | "included">,
): CasesDeserialized["data"] =>
  deserializeJsonApi<CasesDeserialized>(data).data.filter(
    (c) => c.case_properties.status !== CLOSED_STATUS,
  );

const entityKey = (entityType: string, entityId: string) =>
  `${entityType}:${entityId}`;

export const getMappedEntities = (
  caseProperties: Record<string, CasePropertiesValue>,
  watchedValues: Record<string, unknown>,
): MappedEntity[] => {
  const seen = new Set<string>();
  return Object.entries(caseProperties).flatMap(([propertyKey, property]) => {
    if (property.type !== "entity_ref") return [];
    const entityId = watchedValues[propertyKey];
    if (typeof entityId !== "string" || entityId === "") return [];
    const deduplicationKey = entityKey(property.entity_type, entityId);
    if (seen.has(deduplicationKey)) return [];
    seen.add(deduplicationKey);
    return [{ entityType: property.entity_type, entityId }];
  });
};

export const buildQuerySpecs = (
  caseProperties: Record<string, CasePropertiesValue>,
  mappedEntities: MappedEntity[],
): QuerySpec[] =>
  mappedEntities.flatMap((entity) =>
    Object.entries(caseProperties)
      .filter(
        ([, property]) =>
          property.type === "entity_ref" &&
          property.entity_type === entity.entityType,
      )
      .map(([propertyKey]) => ({
        entityType: entity.entityType,
        entityId: entity.entityId,
        propertyKey,
      })),
  );

export const useOpenCasesForMappedEntities = (
  caseProperties: Record<string, CasePropertiesValue> | undefined,
): {
  entities: EntityWithOpenCases[];
  isLoading: boolean;
} => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const apiClient = useCasesClient();
  const [env] = useEnvironment();
  const watchedValues = useWatch<CreateCaseFormValues, "case_properties">({
    name: "case_properties",
  });

  const environment =
    env === "sandbox"
      ? GetCasesV2ApiV2CasesGetFilterEnvEnum.SANDBOX
      : GetCasesV2ApiV2CasesGetFilterEnvEnum.LIVE;

  const mappedEntities =
    caseProperties && watchedValues
      ? getMappedEntities(caseProperties, watchedValues)
      : [];

  const querySpecs = caseProperties
    ? buildQuerySpecs(caseProperties, mappedEntities)
    : [];

  const casesQueries = useQueries({
    queries: querySpecs.map(({ entityType, entityId, propertyKey }) => {
      const filterKey = `filter[properties.${propertyKey}+entity_ref][eq]`;
      const propertyFilters = { [filterKey]: entityId };

      return {
        queryKey: casesKeys.list(wsId, environment, propertyFilters),
        queryFn: async ({ signal }: { signal: AbortSignal }) => {
          if (!apiClient) throw new Error("API client not available");

          const response = await apiClient.getCasesV2ApiV2CasesGet(
            {
              filterEnv: environment,
              sort: "-created_at",
              pageSize: 200,
              filterColumnEntityType: entityType,
              filterProperties: propertyFilters,
            },
            { signal },
          );
          return response.data;
        },
        select: deserializeOpenCases,
        staleTime: STALE_TIME,
        enabled: !!apiClient && !!entityId,
      };
    }),
  });

  const { displayMap } = useEntityDisplayValues({
    entityRefs: mappedEntities.map((entity) => ({
      id: entity.entityId,
      entity_type: entity.entityType,
    })),
    environment: env,
  });

  const groupsByEntity = querySpecs.reduce<Record<string, PropertyCaseGroup[]>>(
    (acc, spec, queryIndex) => {
      const matchingCases = casesQueries[queryIndex]?.data ?? [];
      if (matchingCases.length === 0) return acc;

      const key = entityKey(spec.entityType, spec.entityId);
      return {
        ...acc,
        [key]: [
          ...(acc[key] ?? []),
          {
            propertyKey: spec.propertyKey,
            propertyDisplayName:
              caseProperties?.[spec.propertyKey]?.display_name ??
              spec.propertyKey,
            cases: matchingCases,
          },
        ],
      };
    },
    {},
  );

  const entities: EntityWithOpenCases[] = mappedEntities
    .map((entity) => ({
      entityId: entity.entityId,
      entityType: entity.entityType,
      displayName: displayMap[entity.entityId] ?? entity.entityId,
      groups:
        groupsByEntity[entityKey(entity.entityType, entity.entityId)] ?? [],
    }))
    .filter((entry) => entry.groups.length > 0);

  return { entities, isLoading: casesQueries.some((q) => q.isLoading) };
};
